/* ══════════════════════════════════════════════════════════════
   preferences.ts — Notification Preferences Manager
   Per-type toggles with IST quiet hours support
   ══════════════════════════════════════════════════════════════ */

export interface QuietHoursConfig {
    enabled: boolean;
    start: number;  // Hour in IST (0-23), e.g., 22 = 10 PM
    end: number;    // Hour in IST (0-23), e.g., 7 = 7 AM
}

export interface AlertTypePreferences {
    circuit_breaker: boolean;
    fii_flow: boolean;
    strategy_signal: boolean;
    earnings_result: boolean;
    market_open: boolean;
    market_close: boolean;
    price_alert: boolean;
    news_breaking: boolean;
}

export interface NotificationPreferences {
    enabled: boolean;
    alertTypes: AlertTypePreferences;
    quietHours: QuietHoursConfig;
    channels: {
        browser: boolean;
        email: boolean;
        telegram: boolean;
        whatsapp: boolean;
    };
    emailFrequency: 'daily' | 'weekly' | 'none';
}

const STORAGE_KEY = 'imm-notification-prefs';

const DEFAULT_PREFERENCES: NotificationPreferences = {
    enabled: false,
    alertTypes: {
        circuit_breaker: true,
        fii_flow: true,
        strategy_signal: true,
        earnings_result: true,
        market_open: false,
        market_close: false,
        price_alert: true,
        news_breaking: false,
    },
    quietHours: {
        enabled: true,
        start: 22,  // 10 PM IST
        end: 7,     // 7 AM IST
    },
    channels: {
        browser: true,
        email: false,
        telegram: false,
        whatsapp: false,
    },
    emailFrequency: 'daily',
};

let currentPreferences: NotificationPreferences = { ...DEFAULT_PREFERENCES };

/**
 * Load preferences from localStorage
 */
export function loadNotificationPreferences(): NotificationPreferences {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored) as Partial<NotificationPreferences>;
            currentPreferences = {
                ...DEFAULT_PREFERENCES,
                ...parsed,
                alertTypes: { ...DEFAULT_PREFERENCES.alertTypes, ...parsed.alertTypes },
                quietHours: { ...DEFAULT_PREFERENCES.quietHours, ...parsed.quietHours },
                channels: { ...DEFAULT_PREFERENCES.channels, ...parsed.channels },
            };
        }
    } catch {
        console.warn('[NotifPrefs] Failed to load preferences, using defaults');
        currentPreferences = { ...DEFAULT_PREFERENCES };
    }
    return currentPreferences;
}

/**
 * Save preferences to localStorage
 */
export function saveNotificationPreferences(prefs: NotificationPreferences): void {
    currentPreferences = prefs;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
        console.warn('[NotifPrefs] Failed to save preferences');
    }
}

/**
 * Get current notification preferences
 */
export function getNotificationPreferences(): NotificationPreferences {
    return { ...currentPreferences };
}

/**
 * Update a single alert type toggle
 */
export function setAlertTypeEnabled(alertType: keyof AlertTypePreferences, enabled: boolean): void {
    currentPreferences.alertTypes[alertType] = enabled;
    saveNotificationPreferences(currentPreferences);
}

/**
 * Toggle notifications globally
 */
export function setNotificationsEnabled(enabled: boolean): void {
    currentPreferences.enabled = enabled;
    saveNotificationPreferences(currentPreferences);
}

/**
 * Update quiet hours config
 */
export function setQuietHours(config: Partial<QuietHoursConfig>): void {
    currentPreferences.quietHours = { ...currentPreferences.quietHours, ...config };
    saveNotificationPreferences(currentPreferences);
}

/**
 * Render notification preferences UI section
 */
export function renderNotificationPreferences(): string {
    const prefs = getNotificationPreferences();
    const alertLabels: Record<keyof AlertTypePreferences, { label: string; emoji: string }> = {
        circuit_breaker: { label: 'Circuit Breaker Alerts', emoji: '🚨' },
        fii_flow: { label: 'FII/DII Flow Updates', emoji: '💰' },
        strategy_signal: { label: 'Strategy Signals', emoji: '📈' },
        earnings_result: { label: 'Earnings Results', emoji: '📊' },
        market_open: { label: 'Market Open', emoji: '🔔' },
        market_close: { label: 'Market Close', emoji: '🔕' },
        price_alert: { label: 'Price Alerts', emoji: '💹' },
        news_breaking: { label: 'Breaking News', emoji: '📰' },
    };

    const alertToggles = Object.entries(alertLabels).map(([key, { label, emoji }]) => {
        const enabled = prefs.alertTypes[key as keyof AlertTypePreferences];
        return `
            <div style="display: flex; justify-content: space-between; align-items: center; padding: var(--space-1) 0;">
                <span class="text-sm">${emoji} ${label}</span>
                <button class="glass-btn glass-btn--sm notif-toggle" data-alert-type="${key}"
                    style="min-width: 48px; ${enabled ? 'background: var(--color-emerald); color: white; border-color: var(--color-emerald);' : ''}">${enabled ? 'ON' : 'OFF'}</button>
            </div>`;
    }).join('');

    return `
        <div class="glass-card">
            <h3 style="margin-bottom: var(--space-4);">🔔 Notification Preferences</h3>
            <div style="display: flex; justify-content: space-between; align-items: center; padding: var(--space-2) 0; margin-bottom: var(--space-3); border-bottom: 1px solid rgba(255,255,255,0.1);">
                <span class="text-sm font-semibold">Enable Notifications</span>
                <button class="glass-btn glass-btn--sm" id="notif-master-toggle"
                    style="min-width: 48px; ${prefs.enabled ? 'background: var(--color-emerald); color: white; border-color: var(--color-emerald);' : ''}">${prefs.enabled ? 'ON' : 'OFF'}</button>
            </div>
            <div style="display: flex; flex-direction: column; gap: var(--space-1);">
                ${alertToggles}
            </div>
            <div style="margin-top: var(--space-4); padding-top: var(--space-3); border-top: 1px solid rgba(255,255,255,0.1);">
                <span class="text-xs text-muted">🌙 Quiet Hours: ${prefs.quietHours.enabled ? `${prefs.quietHours.start}:00 – ${prefs.quietHours.end}:00 IST` : 'Disabled'}</span>
            </div>
        </div>
    `;
}

/**
 * Initialize notification preference toggles (call after DOM render)
 */
export function initNotificationPreferenceToggles(): void {
    // Master toggle
    const masterToggle = document.getElementById('notif-master-toggle');
    if (masterToggle) {
        masterToggle.addEventListener('click', () => {
            const prefs = getNotificationPreferences();
            setNotificationsEnabled(!prefs.enabled);
            // Re-render
            const container = masterToggle.closest('.glass-card');
            if (container) {
                container.outerHTML = renderNotificationPreferences();
                initNotificationPreferenceToggles();
            }
        });
    }

    // Per-alert toggles
    document.querySelectorAll('.notif-toggle').forEach(btn => {
        btn.addEventListener('click', () => {
            const alertType = (btn as HTMLElement).dataset.alertType as keyof AlertTypePreferences;
            if (!alertType) return;
            const prefs = getNotificationPreferences();
            setAlertTypeEnabled(alertType, !prefs.alertTypes[alertType]);
            // Re-render
            const container = btn.closest('.glass-card');
            if (container) {
                container.outerHTML = renderNotificationPreferences();
                initNotificationPreferenceToggles();
            }
        });
    });
}

// Load on module init
loadNotificationPreferences();
